import { z } from 'zod';

const AmountRawSchema = z.string().regex(/^\d+$/).transform(BigInt);

export const POINTS_EVENT_TYPES = [
  'trade_executed',
  'trade_referral',
  'referral_attached',
  'limit_order_opened',
  'limit_order_filled',
  'vault_deposited',
  'quest_invite_friends',
  'quest_swap_volume',
  'quest_polymarket_deposit',
  'quest_place_limit_order',
  'quest_completed',
  'admin_adjustment',
  'points_redeemed',
  'points_redemption_refunded',
] as const;

const POINTS_EVENT_TYPES_WITH_UNKNOWN = [...POINTS_EVENT_TYPES, 'unknown'] as const;

export type PointsEventType = (typeof POINTS_EVENT_TYPES_WITH_UNKNOWN)[number];

export const PointsEventTypeSchema = z.enum(POINTS_EVENT_TYPES_WITH_UNKNOWN).catch('unknown');

export const PointsHistoryEntrySchema = z.object({
  id: z.string(),
  eventType: PointsEventTypeSchema,
  deltaPoints: z.coerce.number(),
  refId: z.string(),
  source: z.string().nullable(),
  commissionUsd: z.number().nullable(),
  createdAt: z.string(),
});

export const PointsHistorySchema = z.object({
  items: z.array(PointsHistoryEntrySchema),
  nextCursor: z.string().nullable(),
});

export const REDEMPTION_STATUSES = ['pending', 'processing', 'paid', 'failed'] as const;

const REDEMPTION_STATUSES_WITH_UNKNOWN = [...REDEMPTION_STATUSES, 'unknown'] as const;

export type RedemptionStatus = (typeof REDEMPTION_STATUSES_WITH_UNKNOWN)[number];

export const RedemptionStatusSchema = z.enum(REDEMPTION_STATUSES_WITH_UNKNOWN).catch('unknown');

export const RedemptionSchema = z.object({
  id: z.string(),
  points: z.number().int(),
  rate: z.string(),
  usdcAmountRaw: AmountRawSchema,
  network: z.string(),
  tokenAddress: z.string(),
  recipientAddress: z.string(),
  status: RedemptionStatusSchema,
  txHash: z.string().nullable(),
  failureReason: z.string().nullable(),
  paidAt: z.string().nullable(),
  createdAt: z.string(),
});

export const RedemptionListSchema = z.object({
  items: z.array(RedemptionSchema),
  total: z.number().int(),
  limit: z.number().int(),
  offset: z.number().int(),
});

export const SupportedRedemptionNetworkSchema = z.object({
  network: z.string(),
  tokenAddress: z.string(),
  label: z.string(),
});

export const RedeemInfoSchema = z.object({
  rate: z.string(),
  supportedNetworks: z.array(SupportedRedemptionNetworkSchema),
});

export type PointsHistoryEntry = z.infer<typeof PointsHistoryEntrySchema>;
export type PointsHistory = z.infer<typeof PointsHistorySchema>;
export type Redemption = z.infer<typeof RedemptionSchema>;
export type RedemptionList = z.infer<typeof RedemptionListSchema>;
export type SupportedRedemptionNetwork = z.infer<typeof SupportedRedemptionNetworkSchema>;
export type RedeemInfo = z.infer<typeof RedeemInfoSchema>;
